import { useState } from "react";
import { Pencil, Camera, X, Check } from "lucide-react";
import { useContent } from "@/lib/content";
import { api, resolveImage } from "@/lib/api";
import ImageCropper from "@/components/ImageCropper";
import { toast } from "sonner";

// Inline-editable text. Falls back to `fallback` when no content value set.
// Renders inside `as` element (default: span). `multiline` switches to textarea modal.
export function EditableText({
  contentKey,
  fallback = "",
  as: Tag = "span",
  className = "",
  multiline = false,
  children,
  ...rest
}) {
  const { content, isAdmin, setField } = useContent();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const value = content[contentKey];
  const display = value != null && value !== "" ? value : fallback;

  const openEdit = () => {
    setDraft(display || "");
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await setField(contentKey, draft);
      setEditing(false);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Tag className={`${className} ${isAdmin ? "relative group/edit" : ""}`} {...rest}>
        {children ? children(display) : display}
        {isAdmin && (
          <button
            type="button"
            onClick={openEdit}
            title="Edit text"
            data-testid={`edit-${contentKey}`}
            className="no-print absolute -top-2 -right-2 h-7 w-7 rounded-full bg-[var(--ark-accent)] text-white flex items-center justify-center opacity-0 group-hover/edit:opacity-100 transition-opacity shadow-md z-10"
          >
            <Pencil size={12} />
          </button>
        )}
      </Tag>
      {editing && (
        <div
          data-testid="edit-text-modal"
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
        >
          <div className="bg-white w-full max-w-xl">
            <div className="flex items-center justify-between border-b border-[var(--ark-border)] px-5 py-3">
              <div>
                <div className="font-display text-xl">Edit text</div>
                <div className="text-[0.65rem] uppercase tracking-[0.16em] text-[var(--ark-muted)]">
                  {contentKey}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="p-2 hover:text-[var(--ark-accent)]"
                aria-label="Cancel"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              {multiline ? (
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={6}
                  className="ark-input !border-b min-h-[140px]"
                  autoFocus
                />
              ) : (
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="ark-input !border-b"
                  autoFocus
                />
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[var(--ark-border)]">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="text-[0.72rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] hover:text-black"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="ark-btn"
                data-testid="edit-text-save"
              >
                <Check size={14} className="mr-2" />
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Editable image. `aspect` controls the crop box (default 4/5). Shows the
// rendered child image; on admin hover, click to upload+crop a replacement.
export function EditableImage({
  contentKey,
  fallback = "",
  aspect = 4 / 5,
  alt = "",
  className = "",
  imgClassName = "",
  wrapperTestId,
}) {
  const { content, isAdmin, setField } = useContent();
  const [cropSrc, setCropSrc] = useState(null);
  const [uploading, setUploading] = useState(false);

  const value = content[contentKey];
  const src = value != null && value !== "" ? value : fallback;

  const onPick = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const uploadCropped = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await setField(contentKey, data.url);
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setCropSrc(null);
    }
  };

  return (
    <div
      data-testid={wrapperTestId || `image-${contentKey}`}
      className={`${className} ${isAdmin ? "relative group/img" : ""}`}
    >
      <img src={resolveImage(src)} alt={alt} className={imgClassName} />
      {isAdmin && (
        <label
          title="Replace photo"
          data-testid={`edit-img-${contentKey}`}
          className="absolute top-3 right-3 h-9 rounded-full bg-[var(--ark-accent)] text-white flex items-center gap-2 px-3 opacity-0 group-hover/img:opacity-100 transition-opacity shadow-md cursor-pointer text-[0.65rem] uppercase tracking-[0.14em]"
        >
          <Camera size={13} />
          {uploading ? "Uploading" : "Replace"}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) onPick(f);
            }}
          />
        </label>
      )}
      {cropSrc && (
        <ImageCropper
          src={cropSrc}
          aspect={aspect}
          onCancel={() => setCropSrc(null)}
          onCropped={async (file) => {
            setCropSrc(null);
            await uploadCropped(file);
          }}
        />
      )}
    </div>
  );
}
